import { type Parameters } from '@inpart-io/devtools-iap';
import {
    CASCADE_DELETE_DATA_OBJECT,
    RECEIVE_DATA_OBJECTS,
    REMOVE_CHECK_CASCADE_DELETION_STATUS,
    REMOVE_DATA_OBJECTS,
    STORE_DATA_OBJECTS
} from '@inpart-io/devtools-vue';
import {
    cascadeDeleteExecutionManager,
    type ICascadeDeleteAsyncExecStatusResult,
    type IObligation,
    type ITriggeringEvents,
    obligationsManager
} from '@inpart-io/padme-iap';
import { type ActionTree } from 'vuex';

import { TRIGGERING_EVENTS_MODULE } from '../triggering-events-module';
import { RECEIVE_RESPONSIBILITIES } from './mutations';

export const RETRIEVE_RESPONSIBILITIES = 'retrieveResponsibilities';

export const CREATE_OBLIGATION = 'createObligation';

export const actions: ActionTree<IObligation, unknown> = {
    async [RETRIEVE_RESPONSIBILITIES](context) {
        const result = await obligationsManager.getObligationResponsibilities();
        if (result) {
            context.commit(RECEIVE_RESPONSIBILITIES, { responsibilities: result });
        }
    },

    async [CREATE_OBLIGATION](
        context,
        { obligation, triggeringEvents, properties }: { obligation: IObligation; triggeringEvents?: ITriggeringEvents; properties?: string }
    ): Promise<number> {
        const params: Parameters = {
            properties
        };

        const newObligation = await obligationsManager.createObligation(obligation, triggeringEvents, params);
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [newObligation] });

        return newObligation.dobjId;
    },

    // Cascade delete action for obligations
    // This action will trigger the cascade delete process and wait for its completion.
    // This action will override the default one coming from toolbox
    async [CASCADE_DELETE_DATA_OBJECT](context, { dobjId, properties }: { dobjId: number; properties?: string }): Promise<ICascadeDeleteAsyncExecStatusResult> {
        const asyncStatus = await obligationsManager.cascadeDelete(dobjId, { properties });

        const result = await cascadeDeleteExecutionManager.waitForStatus(asyncStatus.statusId);

        context.commit(REMOVE_DATA_OBJECTS, { dobjId });
        context.commit(REMOVE_CHECK_CASCADE_DELETION_STATUS, { dobjId });

        // Add an additional 1000 ms delay before returning the result, give time for the BE to process the deletion
        await new Promise((resolve) => {
            return setTimeout(resolve, 1000);
        });

        return result;
    },

    [STORE_DATA_OBJECTS](context, { dataObjectList }: { dataObjectList: IObligation[] }) {
        const obligations: IObligation[] = [];
        const triggeringEvents: ITriggeringEvents[] = [];

        dataObjectList.forEach((obligation) => {
            let _obligation = Object.assign({}, obligation);

            if (obligation.triggeringEvent !== null && obligation.triggeringEvent !== undefined && typeof obligation.triggeringEvent !== 'number') {
                const triggeringEvent: ITriggeringEvents = Object.assign({}, obligation.triggeringEvent);
                _obligation = Object.assign({}, obligation, { triggeringEvent: triggeringEvent.dobjId });
                triggeringEvents.push(triggeringEvent);
            }

            obligations.push(_obligation);
        });

        if (obligations.length) {
            context.commit(RECEIVE_DATA_OBJECTS, { dataObjectList: obligations });
        }
        if (triggeringEvents.length) {
            void context.dispatch(`${TRIGGERING_EVENTS_MODULE}/${STORE_DATA_OBJECTS}`, { dataObjectList: triggeringEvents }, { root: true });
        }
    }
};
